import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { api } from "../../api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { navigate } from "../../lib/router";
import { useLanguage } from "../../hooks/useLanguage";
import { ArrowRight } from "lucide-react";
import logoIcon from "../../assets/icone.png";

export function LoginForm({ onSwitch, onSuccess }: { onSwitch?: () => void; onSuccess?: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const { t } = useLanguage();
  const lbl = t.auth.login;

  useEffect(() => {
    api.me().then(res => {
      if (res.ok) {
        navigate("/app");
      } else {
        // Se houver erro ao verificar autenticação, faz logout local
        api.logout().catch(() => {
          // Ignora erros do logout, apenas garante limpeza local
        });
      }
    }).catch(() => {
      // Se houver erro de rede, faz logout local
      api.logout().catch(() => {
        // Ignora erros do logout
      });
    });
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setLoading(true);
    try {
      const res = await api.login({ email, password });
      if (res.ok) {
        setMessage(lbl.success);
        navigate("/app");
      } else {
        setMessage(res.error);
      }
    } finally {
      setLoading(false);
      onSuccess?.();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white selection:bg-indigo-100 selection:text-indigo-900 font-sans relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-neutral-900/10 via-white to-white -z-10" />
      <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 -z-10 mix-blend-overlay"></div>

      <div className="max-w-md w-full mx-4">
        {/* Logo/Brand */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <img src={logoIcon} alt="Hub Links Logo" className="w-10 h-10 rounded-xl" />
          <span className="text-2xl font-bold tracking-tight text-slate-900">Hub Links</span>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl border-2 border-slate-100 p-8 shadow-xl shadow-slate-900/5">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mb-2">
              {lbl.title}
            </h1>
            <p className="text-slate-600">
              {lbl.subtitle}
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold text-slate-700">
                {lbl.email.label}
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={lbl.email.placeholder}
                className="h-12 rounded-xl border-slate-200 focus:border-brand-primary focus:ring-brand-primary"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold text-slate-700">
                {lbl.password.label}
              </Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={lbl.password.placeholder}
                className="h-12 rounded-xl border-slate-200 focus:border-[#0DAFFD] focus:ring-[#0DAFFD]"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-[#0DAFFD] to-[#0FD8FE] hover:from-[#0DAFFD] hover:to-[#0FD8FE] text-white font-semibold shadow-lg shadow-[#0DAFFD]/20 hover:shadow-xl hover:shadow-[#0DAFFD]/30 transition-all hover:-translate-y-0.5"
            >
              {loading ? lbl.loading : lbl.submit}
              {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
            </Button>
          </form>

          {message && (
            <div
              className={`mt-4 p-3 rounded-xl text-sm font-medium ${message.includes("!")
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-red-50 text-red-700 border border-red-200"
                }`}
            >
              {message}
            </div>
          )}

          <p className="mt-6 text-sm text-center text-slate-600">
            {lbl.switchPrompt}
            <Button
              variant="link"
              onClick={onSwitch}
              className="ml-1 font-semibold text-indigo-600 hover:text-indigo-700 underline underline-offset-2"
            >
              {lbl.switchCta}
            </Button>
          </p>
        </div>
      </div>
    </div>
  );
}
